import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import {
  MEET_SAM_INTRO,
  PREVIEW_DURATION,
  acquirePreview,
  prefersReducedMotion,
  releasePreview,
} from "./videoDiaries";

const PREVIEW_TOKEN = "meet-sam-intro";

/** Tailwind `sm` breakpoint mirror for JS (640px). */
function useIsDesktop() {
  const [desktop, setDesktop] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(min-width: 640px)").matches
      : true
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = (e) => setDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return desktop;
}

/**
 * Themed fullscreen overlay (desktop only). Styled after the mission-patch
 * frame: dark charcoal panel, thin orange border, circular ring around the
 * video, mono label top-left, status line bottom. Never preventDefault() on
 * inner clicks — only stopPropagation for close-on-backdrop.
 */
function MeetSamOverlay({ onClose }) {
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play()?.catch(() => {});
    else el.pause();
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <div
      data-testid="meet-sam-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#060a13]/92 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Meet Sam — fullscreen"
    >
      <div
        data-testid="meet-sam-overlay-frame"
        className="relative w-full max-w-4xl rounded-lg border border-[#ff8c46]/60 bg-[#141418] p-4 shadow-[0_0_60px_rgba(255,140,70,0.15)] sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <p
          data-testid="meet-sam-overlay-label"
          className="mono absolute left-4 top-3 text-[10px] font-bold tracking-[0.25em] text-[#ff8c46]"
        >
          MEET SAM // COMMANDER LOG
        </p>
        <button
          type="button"
          data-testid="meet-sam-overlay-close"
          aria-label="Close fullscreen"
          onClick={onClose}
          className="absolute right-3 top-2.5 text-slate-400 transition-colors hover:text-white"
        >
          <X size={18} />
        </button>

        {/* 16:9 video frame — thin inner border, rivet corners like the patch panel */}
        <div className="relative mx-auto mt-7 w-full max-w-[86vw] sm:max-w-[min(80vw,140vh)]">
          <span className="absolute -left-1 -top-1 h-1.5 w-1.5 rounded-full bg-[#3a3a42]" />
          <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-[#3a3a42]" />
          <span className="absolute -bottom-1 -left-1 h-1.5 w-1.5 rounded-full bg-[#3a3a42]" />
          <span className="absolute -bottom-1 -right-1 h-1.5 w-1.5 rounded-full bg-[#3a3a42]" />
          <video
            ref={videoRef}
            data-testid="meet-sam-overlay-player"
            className="w-full rounded-sm border border-[#3a3a42] bg-black object-contain"
            style={{ aspectRatio: "16 / 9" }}
            src={MEET_SAM_INTRO.src}
            poster={MEET_SAM_INTRO.poster}
            autoPlay
            playsInline
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
            onClick={togglePlay}
          />
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-[#2a2a30] pt-3">
          <p className="mono text-[9px] tracking-[0.3em] text-slate-500">
            SECTOR 7 · DEEP SPACE UPLINK
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="meet-sam-overlay-play-btn"
              aria-label={paused ? "Play" : "Pause"}
              className="mono flex items-center gap-1.5 border border-[#1c2740] px-2 py-1 text-[10px] tracking-[0.2em] text-slate-300 hover:border-gmh-cyan/50"
              onClick={togglePlay}
            >
              {paused ? <Play size={12} /> : <Pause size={12} />}
              {paused ? "PLAY" : "PAUSE"}
            </button>
            <button
              type="button"
              data-testid="meet-sam-overlay-mute-btn"
              aria-label={muted ? "Unmute" : "Mute"}
              className="mono flex items-center gap-1.5 border border-[#1c2740] px-2 py-1 text-[10px] tracking-[0.2em] text-slate-300 hover:border-gmh-cyan/50"
              onClick={toggleMute}
            >
              {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
              {muted ? "MUTED" : "SOUND"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MeetSamIntro() {
  const videoRef = useRef(null);
  const rootRef = useRef(null);
  const modeRef = useRef("preview");
  const reduced = prefersReducedMotion();
  const isDesktop = useIsDesktop();
  const [mode, setMode] = useState(reduced ? "poster" : "preview");
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(true);
  const [unmuteFailed, setUnmuteFailed] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  modeRef.current = mode;

  const startMutedPreview = useCallback(() => {
    const el = videoRef.current;
    if (!el || prefersReducedMotion()) return;
    if (!acquirePreview(PREVIEW_TOKEN)) return;
    el.muted = true;
    el.defaultMuted = true;
    const playAttempt = el.play();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {
        setMode("poster");
        releasePreview(PREVIEW_TOKEN);
      });
    }
  }, []);

  useEffect(() => {
    if (reduced) return undefined;
    startMutedPreview();
    return () => releasePreview(PREVIEW_TOKEN);
  }, [reduced, startMutedPreview]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) videoRef.current?.pause();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver !== "function") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        const el = videoRef.current;
        if (!el) return;
        if (!entry.isIntersecting) {
          el.pause();
          return;
        }
        if (modeRef.current === "poster" || prefersReducedMotion()) return;
        if (modeRef.current === "preview" && el.currentTime < PREVIEW_DURATION) {
          el.muted = true;
          el.play()?.catch(() => {});
        } else if (modeRef.current === "playing") {
          el.play()?.catch(() => {});
        }
      },
      { threshold: 0.25 }
    );
    io.observe(root);
    return () => io.disconnect();
  }, []);

  const engageWithSound = async () => {
    const el = videoRef.current;
    if (!el) return;
    setMode("playing");
    modeRef.current = "playing";
    setUnmuteFailed(false);
    el.muted = false;
    try {
      await el.play();
      if (el.muted) {
        setUnmuteFailed(true);
        setMuted(true);
      } else {
        setMuted(false);
      }
    } catch {
      el.muted = true;
      setMuted(true);
      setUnmuteFailed(true);
      try {
        await el.play();
      } catch {
        /* keep poster/play control */
      }
    }
  };

  const onTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    if (modeRef.current === "preview" && el.currentTime >= PREVIEW_DURATION) {
      el.pause();
      if (el.currentTime > PREVIEW_DURATION) el.currentTime = PREVIEW_DURATION;
    }
  };

  const onPlay = () => setPaused(false);
  const onPause = () => setPaused(true);

  const onSurfaceClick = () => {
    if (mode === "preview" || mode === "poster" || muted) {
      engageWithSound();
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play()?.catch(() => {});
    else el.pause();
  };

  const onPlayBtn = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    if (mode === "preview" || mode === "poster") {
      engageWithSound();
      return;
    }
    if (el.paused) el.play()?.catch(() => {});
    else el.pause();
  };

  const onMuteBtn = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    if (muted) {
      engageWithSound();
      return;
    }
    el.muted = true;
    setMuted(true);
  };

  const onFullscreenBtn = (e) => {
    e.stopPropagation();
    if (isDesktop) {
      // Themed overlay (desktop). The overlay video autoplays with sound —
      // allowed because this is a direct user gesture.
      setOverlayOpen(true);
      return;
    }
    // Mobile: native fullscreen (iOS Safari forces its own player anyway).
    const el = videoRef.current;
    if (!el) return;
    if (typeof el.requestFullscreen === "function") {
      el.requestFullscreen()?.catch?.(() => {});
    } else if (typeof el.webkitEnterFullscreen === "function") {
      el.webkitEnterFullscreen();
    }
  };

  const showPoster = mode === "poster";

  const restartMutedPreview = () => {
    // Restart muted preview on hover (never unmute — click/play does that).
    if (prefersReducedMotion()) return;
    const el = videoRef.current;
    if (!el || modeRef.current === "playing") return;
    if (modeRef.current === "poster") {
      setMode("preview");
      modeRef.current = "preview";
    }
    el.muted = true;
    setMuted(true);
    if (el.currentTime >= PREVIEW_DURATION) el.currentTime = 0;
    acquirePreview(PREVIEW_TOKEN);
    el.play()?.catch(() => {});
  };

  return (
    <div
      ref={rootRef}
      data-testid="meet-sam-intro"
      data-state={mode}
      className="relative min-w-0"
      onMouseEnter={restartMutedPreview}
    >
      {showPoster ? (
        <img
          data-testid="sam-portrait"
          src={MEET_SAM_INTRO.poster}
          alt="Commander Sam"
          className="mx-auto h-auto w-full max-h-[22rem] object-contain object-bottom"
        />
      ) : null}
      <video
        ref={(node) => {
          videoRef.current = node;
          if (node && modeRef.current === "preview") {
            node.muted = true;
            node.defaultMuted = true;
          }
        }}
        data-testid="meet-sam-intro-player"
        className={`mx-auto h-auto w-full max-h-[22rem] lg:max-h-[28rem] border border-[#1c2740] bg-black object-contain ${
          showPoster ? "sr-only" : ""
        }`}
        src={MEET_SAM_INTRO.src}
        poster={MEET_SAM_INTRO.poster}
        muted={muted}
        playsInline
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onPlay={onPlay}
        onPause={onPause}
        onClick={onSurfaceClick}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="meet-sam-play-btn"
          aria-label={paused ? "Play" : "Pause"}
          className="mono flex items-center gap-1.5 border border-[#1c2740] px-2 py-1 text-[10px] tracking-[0.2em] text-slate-300 hover:border-gmh-cyan/50"
          onClick={onPlayBtn}
        >
          {paused ? <Play size={12} /> : <Pause size={12} />}
          {paused ? "PLAY" : "PAUSE"}
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="meet-sam-mute-btn"
            aria-label={muted ? "Unmute" : "Mute"}
            className="mono flex items-center gap-1.5 border border-[#1c2740] px-2 py-1 text-[10px] tracking-[0.2em] text-slate-300 hover:border-gmh-cyan/50"
            onClick={onMuteBtn}
          >
            {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            {muted ? "MUTED" : "SOUND"}
          </button>
          <button
            type="button"
            data-testid="meet-sam-fullscreen-btn"
            aria-label="Fullscreen"
            title="Fullscreen"
            className="mono flex items-center gap-1.5 border border-[#1c2740] px-2 py-1 text-[10px] tracking-[0.2em] text-slate-300 hover:border-gmh-cyan/50"
            onClick={onFullscreenBtn}
          >
            <Maximize2 size={12} />
          </button>
        </div>
      </div>
      {overlayOpen ? <MeetSamOverlay onClose={() => setOverlayOpen(false)} /> : null}
      {unmuteFailed ? (
        <p data-testid="meet-sam-unmute-hint" className="mt-1 text-center text-[11px] text-slate-500">
          Tap unmute for audio
        </p>
      ) : null}
    </div>
  );
}
